import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../../components/Layout/Layout';
import Seo from '../../components/Seo';
import SoftwareBoxMockup from '../../components/HomeProducts/SoftwareBoxMockup';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

// Robust fallback products matching Orbitronix platform catalog
const FALLBACK_PRODUCTS = [
    {
        id: 1,
        category_id: 1,
        name_ka: 'ელექტრონული მუნიციპალიტეტი & სერვისები',
        name_en: 'Municipal Citizen Portal & Services',
        slug: 'municipal-citizen-portal',
        tagline_ka: 'მოქალაქეთა ონლაინ განცხადებები და მუნიციპალური სერვისები',
        tagline_en: 'Citizen applications, digital queue and administrative services',
        icon: '🏛️',
        badge_color: '#2563eb',
        target_market: 'municipal',
        setup_price: '18000.00',
        annual_price: '6000.00',
        tech_stack: 'React, Node.js, PostgreSQL, Docker',
        features_ka: ['ონლაინ განცხადებების პორტალი', 'ელექტრონული რიგის მართვა', 'SMS/Email შეტყობინებები', 'მერიის ადმინ პანელი'],
        features_en: ['Online applications portal', 'Digital queue management', 'SMS/Email notifications', 'City Hall admin panel'],
        is_active: 1,
        is_featured: 1,
        sort_order: 1,
        category_name_ka: 'ციფრული მმართველობა',
        category_name_en: 'Digital Governance'
    },
    {
        id: 2,
        category_id: 1,
        name_ka: 'საქმისწარმოების & დოკუმენტბრუნვის სისტემა',
        name_en: 'Electronic Document & Case Management',
        slug: 'electronic-document-flow',
        tagline_ka: 'შიდა მუნიციპალური დოკუმენტბრუნვა და ციფრული ხელმოწერა',
        tagline_en: 'Internal document workflow, digital signature and archival',
        icon: '📑',
        badge_color: '#7c3aed',
        target_market: 'government',
        setup_price: '15000.00',
        annual_price: '5000.00',
        tech_stack: 'React, .NET Core, SQL Server',
        features_ka: ['ციფრული ხელმოწერის მხარდაჭერა', 'ავტომატური ნუმერაცია', 'დავალებების მონიტორინგი', 'როლებზე დაფუძნებული წვდომა'],
        features_en: ['Digital signature integration', 'Automated numbering', 'Task monitoring', 'Role-based access'],
        is_active: 1,
        is_featured: 0,
        sort_order: 2,
        category_name_ka: 'ციფრული მმართველობა',
        category_name_en: 'Digital Governance'
    },
    {
        id: 3,
        category_id: 1,
        name_ka: 'მოქალაქეთა ჩართულობა & პეტიციები',
        name_en: 'Citizen Engagement & Petition Platform',
        slug: 'citizen-engagement',
        tagline_ka: 'რუკაზე დაფუძნებული პრობლემების დაფიქსირება და კენჭისყრა',
        tagline_en: 'Map-based issue reporting, online petitions and public voting',
        icon: '🗣️',
        badge_color: '#059669',
        target_market: 'municipal',
        setup_price: '12000.00',
        annual_price: '4000.00',
        tech_stack: 'React, Leaflet, PHP, MySQL',
        features_ka: ['რუკაზე პრობლემის მონიშვნა', 'ონლაინ პეტიციები', 'საჯარო გამოკითხვები', 'ფოტო-ფიქსაცია'],
        features_en: ['Map-based issue reporting', 'Photo attachment', 'Online petitions', 'Public voting'],
        is_active: 1,
        is_featured: 0,
        sort_order: 3,
        category_name_ka: 'ციფრული მმართველობა',
        category_name_en: 'Digital Governance'
    },
    {
        id: 4,
        category_id: 1,
        name_ka: 'GIS მუნიციპალური რუკა & ზონირება',
        name_en: 'Municipal GIS Mapping & Zoning',
        slug: 'municipal-gis',
        tagline_ka: 'ინტერაქტიული GIS შრეები, ინფრასტრუქტურა, ზონირება',
        tagline_en: 'Interactive GIS layers, infrastructure, zoning visualization',
        icon: '🗺️',
        badge_color: '#0891b2',
        target_market: 'municipal',
        setup_price: '22000.00',
        annual_price: '7500.00',
        tech_stack: 'React, PostGIS, GeoServer, Leaflet',
        features_ka: ['ინტერაქტიული GIS შრეები', 'ზონირების ჩვენება', 'ინფრასტრუქტურის ობიექტები', 'ნებართვების კონტროლი'],
        features_en: ['Interactive GIS layers', 'Zoning visualization', 'Infrastructure objects', 'Permit management'],
        is_active: 1,
        is_featured: 0,
        sort_order: 4,
        category_name_ka: 'ციფრული მმართველობა',
        category_name_en: 'Digital Governance'
    },
    {
        id: 8,
        category_id: 2,
        name_ka: 'მუნიციპალური მობილური აპლიკაცია (iOS / Android)',
        name_en: 'Citizen Mobile App (iOS / Android)',
        slug: 'citizen-mobile-app',
        tagline_ka: 'ყველა მუნიციპალური სერვისი ერთ აპლიკაციაში',
        tagline_en: 'All municipal services in one mobile app',
        icon: '📱',
        badge_color: '#9333ea',
        target_market: 'municipal',
        setup_price: '28000.00',
        annual_price: '9000.00',
        tech_stack: 'React Native, Expo, Node.js, Stripe',
        features_ka: ['ყველა სერვისი ერთ სივრცეში', 'Push შეტყობინებები', 'ციფრული საშვები', 'ონლაინ გადახდები'],
        features_en: ['All services in one place', 'Push notifications', 'Digital passes', 'Online payments'],
        is_active: 1,
        is_featured: 1,
        sort_order: 5,
        category_name_ka: 'მობილური აპლიკაციები',
        category_name_en: 'Mobile Applications'
    },
    {
        id: 6,
        category_id: 1,
        name_ka: 'საგანგებო შეტყობინებები & SMS/Push მაუწყებელი',
        name_en: 'Emergency Broadcast & SMS Alerts',
        slug: 'emergency-alerts',
        tagline_ka: 'მყისიერი კრიზისული SMS, Push, WEB შეტყობინებები',
        tagline_en: 'Instant crisis SMS, Push, and WEB emergency broadcasts',
        icon: '🚨',
        badge_color: '#dc2626',
        target_market: 'municipal',
        setup_price: '9500.00',
        annual_price: '3500.00',
        tech_stack: 'React, Node.js, Twilio, Redis',
        features_ka: ['SMS მასობრივი გაგზავნა', 'Push შეტყობინებები', 'კატეგორიზებული განგაში', 'სამიზნე რაიონები'],
        features_en: ['Mass SMS broadcasting', 'Push alerts', 'Categorized alert tiers', 'Geo-targeted districts'],
        is_active: 1,
        is_featured: 0,
        sort_order: 6,
        category_name_ka: 'ციფრული მმართველობა',
        category_name_en: 'Digital Governance'
    },
    {
        id: 7,
        category_id: 1,
        name_ka: 'ღია ბიუჯეტი & მოქალაქეთა დაფინანსება',
        name_en: 'Open Budget & Citizen Finance',
        slug: 'open-budget',
        tagline_ka: 'ბიუჯეტის ვიზუალიზაცია, მონაწილეობითი ბიუჯეტირება',
        tagline_en: 'Municipal budget visualization and participatory budgeting',
        icon: '📊',
        badge_color: '#4f46e5',
        target_market: 'municipal',
        setup_price: '11000.00',
        annual_price: '3800.00',
        tech_stack: 'React, Chart.js, PHP, MySQL',
        features_ka: ['ბიუჯეტის ინფოგრაფიკა', 'ხარჯების მონიტორინგი', 'მონაწილეობითი ბიუჯეტი', 'მონაცემთა ექსპორტი'],
        features_en: ['Budget infographic charts', 'Expenditure monitoring', 'Participatory budget', 'Data export'],
        is_active: 1,
        is_featured: 0,
        sort_order: 7,
        category_name_ka: 'ციფრული მმართველობა',
        category_name_en: 'Digital Governance'
    }
];

const ProductsPage = () => {
    const { i18n } = useTranslation();
    const { lang: urlLang } = useParams();
    const navigate = useNavigate();
    const currentLang = (urlLang === 'en' || urlLang === 'ka') ? urlLang : 'en';
    const isKa = currentLang === 'ka';

    const [products, setProducts] = useState(FALLBACK_PRODUCTS);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter, Switch & Sort States
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedMarket, setSelectedMarket] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('featured'); // 'featured', 'price-asc', 'price-desc', 'name-asc', 'name-desc', 'arr-asc'
    const [viewMode, setViewMode] = useState('box'); // 'box' (3D software box), 'card' (modern card), 'list' (horizontal list)

    // Language change effect
    useEffect(() => {
        if (currentLang && (currentLang === 'en' || currentLang === 'ka')) {
            i18n.changeLanguage(currentLang);
        }
        window.scrollTo(0, 0);
    }, [currentLang, i18n]);

    // Fetch live products
    useEffect(() => {
        let isMounted = true;
        const fetchCatalog = async () => {
            setLoading(true);
            try {
                const [pRes, cRes] = await Promise.all([
                    fetch(`${API_URL}products.php`).catch(() => null),
                    fetch(`${API_URL}products.php?type=categories`).catch(() => null)
                ]);

                if (pRes && pRes.ok) {
                    const pData = await pRes.json();
                    if (isMounted && Array.isArray(pData) && pData.length > 0) {
                        const active = pData.filter(p => p.is_active === 1 || p.is_active === '1');
                        setProducts(active.length > 0 ? active : pData);
                    }
                }
                if (cRes && cRes.ok) {
                    const cData = await cRes.json();
                    if (isMounted && Array.isArray(cData)) {
                        setCategories(cData);
                    }
                }
            } catch (err) {
                console.error('Error loading products catalog:', err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchCatalog();
        return () => { isMounted = false; };
    }, []);

    // Filter and Sort Processing
    const processedProducts = useMemo(() => {
        let list = [...products];

        // 1. Category Filter
        if (selectedCategory !== 'all') {
            list = list.filter(p => String(p.category_id) === String(selectedCategory));
        }

        // 2. Target Market Filter
        if (selectedMarket !== 'all') {
            list = list.filter(p => p.target_market === selectedMarket);
        }

        // 3. Search Filter
        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase().trim();
            list = list.filter(p => {
                const name = (isKa ? p.name_ka : (p.name_en || p.name_ka) || '').toLowerCase();
                const tagline = (isKa ? p.tagline_ka : (p.tagline_en || p.tagline_ka) || '').toLowerCase();
                const tech = (p.tech_stack || '').toLowerCase();
                const feats = ((isKa ? p.features_ka : p.features_en) || []).join(' ').toLowerCase();
                return name.includes(query) || tagline.includes(query) || tech.includes(query) || feats.includes(query);
            });
        }

        // 4. Sorting logic
        list.sort((a, b) => {
            const priceA = parseFloat(a.setup_price) || 0;
            const priceB = parseFloat(b.setup_price) || 0;
            const arrA = parseFloat(a.annual_price) || 0;
            const arrB = parseFloat(b.annual_price) || 0;
            const nameA = (isKa ? a.name_ka : (a.name_en || a.name_ka) || '').toLowerCase();
            const nameB = (isKa ? b.name_ka : (b.name_en || b.name_ka) || '').toLowerCase();

            switch (sortBy) {
                case 'featured':
                    if (b.is_featured !== a.is_featured) return (b.is_featured || 0) - (a.is_featured || 0);
                    return (a.sort_order || 0) - (b.sort_order || 0);
                case 'price-asc':
                    return priceA - priceB;
                case 'price-desc':
                    return priceB - priceA;
                case 'arr-asc':
                    return arrA - arrB;
                case 'name-asc':
                    return nameA.localeCompare(nameB);
                case 'name-desc':
                    return nameB.localeCompare(nameA);
                default:
                    return 0;
            }
        });

        return list;
    }, [products, selectedCategory, selectedMarket, searchTerm, sortBy, isKa]);

    const getMarketLabel = (market) => {
        if (isKa) {
            switch (market) {
                case 'municipal': return '🏛️ მუნიციპალური';
                case 'government': return '🏢 სამთავრობო';
                case 'business': return '💼 ბიზნეს სექტორი';
                case 'personal': return '👤 პირადი / საჯარო';
                case 'education': return '🎓 განათლება';
                default: return '🌍 ყველა სეგმენტი';
            }
        }
        switch (market) {
            case 'municipal': return '🏛️ Municipal';
            case 'government': return '🏢 Government';
            case 'business': return '💼 Enterprise';
            case 'personal': return '👤 Citizen & Public';
            case 'education': return '🎓 Education';
            default: return '🌍 All Segments';
        }
    };

    const resetFilters = () => {
        setSelectedCategory('all');
        setSelectedMarket('all');
        setSearchTerm('');
        setSortBy('featured');
    };

    return (
        <Layout>
            <Seo 
                title={isKa ? 'პროდუქტების კატალოგი | Orbitronix' : 'Products Catalog & Turnkey Solutions | Orbitronix'}
                description={isKa ? 'Orbitronix-ის მზა ციფრული გადაწყვეტილებები, მუნიციპალური პლატფორმები და პროგრამული პაკეტები.' : 'Orbitronix turnkey enterprise software packages, municipal platforms, and digital solutions catalog.'}
                keywords="orbitronix products, software catalog, municipal software, digital governance, turn-key solutions"
                url={`/${currentLang}/products`}
            />

            <main className="page_content prod-catalog-page pt-150 pb-120">
                {/* Ambient glow backdrop */}
                <div className="prod-ambient-glow" />

                <div className="container">
                    {/* Catalog Header */}
                    <div className="text-center mb-5">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <span className="prod-catalog-eyebrow">
                                <i className="fas fa-boxes me-2" />
                                {isKa ? 'ORBITRONIX პროგრამული ეკოსისტემა' : 'ORBITRONIX SOFTWARE ECOSYSTEM'}
                            </span>
                            <h1 className="prod-catalog-title mt-2 mb-3">
                                {isKa ? 'ციფრული გადაწყვეტილებების & პლატფორმების კატალოგი' : 'Turnkey Digital Platforms & Software Catalog'}
                            </h1>
                            <p className="prod-catalog-desc mx-auto">
                                {isKa 
                                    ? 'სტანდარტიზებული, უსაფრთხო და მრავალჯერადად გამოცდილი მუნიციპალური და კორპორატიული აპლიკაციების პაკეტი — სრული დოკუმენტაციითა და დანერგვის გარანტიით.'
                                    : 'Standardized, fully compliant municipal & enterprise software packages with source code readiness, ISO 27001 certifications, and rapid deployment.'}
                            </p>
                        </motion.div>
                    </div>

                    {/* Interactive Controls Bar: Switch, Filter & Sort */}
                    <div className="prod-controls-card mb-4">
                        <div className="row g-3 align-items-center">
                            
                            {/* Search Input */}
                            <div className="col-lg-4 col-md-6">
                                <div className="prod-search-wrap">
                                    <i className="fas fa-search prod-search-icon" />
                                    <input 
                                        type="text"
                                        className="prod-search-input"
                                        placeholder={isKa ? 'ძებნა სახელით, ფუნქციით, ტექნოლოგიით...' : 'Search by name, feature, tech stack...'}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button className="prod-search-clear" onClick={() => setSearchTerm('')}>
                                            <i className="fas fa-times" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Market Filter */}
                            <div className="col-lg-3 col-md-6">
                                <div className="d-flex align-items-center gap-2">
                                    <label className="prod-ctrl-label d-none d-sm-inline">
                                        {isKa ? 'სეგმენტი:' : 'Market:'}
                                    </label>
                                    <select 
                                        className="prod-select"
                                        value={selectedMarket}
                                        onChange={(e) => setSelectedMarket(e.target.value)}
                                    >
                                        <option value="all">{isKa ? '🌍 ყველა სეგმენტი' : '🌍 All Target Markets'}</option>
                                        <option value="municipal">{isKa ? '🏛️ მუნიციპალური' : '🏛️ Municipal'}</option>
                                        <option value="government">{isKa ? '🏢 სამთავრობო' : '🏢 Government'}</option>
                                        <option value="business">{isKa ? '💼 ბიზნესი & Enterprise' : '💼 Enterprise'}</option>
                                        <option value="personal">{isKa ? '👤 პირადი სერვისები' : '👤 Personal'}</option>
                                    </select>
                                </div>
                            </div>

                            {/* Sort Dropdown */}
                            <div className="col-lg-3 col-md-6">
                                <div className="d-flex align-items-center gap-2">
                                    <label className="prod-ctrl-label d-none d-sm-inline">
                                        {isKa ? 'სორტირება:' : 'Sort By:'}
                                    </label>
                                    <select 
                                        className="prod-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="featured">{isKa ? '⭐ გამორჩეული / რეკომენდებული' : '⭐ Featured First'}</option>
                                        <option value="price-asc">{isKa ? '🏷️ ფასი: დაბლიდან მაღლისკენ' : '🏷️ Setup Price: Low to High'}</option>
                                        <option value="price-desc">{isKa ? '💎 ფასი: მაღლიდან დაბლისკენ' : '💎 Setup Price: High to Low'}</option>
                                        <option value="arr-asc">{isKa ? '📈 წლიური ARR: დაბლიდან' : '📈 Annual ARR: Low to High'}</option>
                                        <option value="name-asc">{isKa ? '🔤 სახელი: ა — ჰ' : '🔤 Name: A to Z'}</option>
                                        <option value="name-desc">{isKa ? '🔤 სახელი: ჰ — ა' : '🔤 Name: Z to A'}</option>
                                    </select>
                                </div>
                            </div>

                            {/* View Mode Switcher (Switching between layouts!) */}
                            <div className="col-lg-2 col-md-6 text-md-end">
                                <div className="prod-view-switch-btn-group" title={isKa ? 'განლაგების გადართვა' : 'Switch Layout View'}>
                                    <button 
                                        className={`view-switch-btn ${viewMode === 'box' ? 'active' : ''}`}
                                        onClick={() => setViewMode('box')}
                                        title={isKa ? '3D პროგრამული ყუთის ხედი' : '3D Software Box View'}
                                    >
                                        <i className="fas fa-box" />
                                    </button>
                                    <button 
                                        className={`view-switch-btn ${viewMode === 'card' ? 'active' : ''}`}
                                        onClick={() => setViewMode('card')}
                                        title={isKa ? 'თანამედროვე ბარათების ხედი' : 'Modern Cards View'}
                                    >
                                        <i className="fas fa-th-large" />
                                    </button>
                                    <button 
                                        className={`view-switch-btn ${viewMode === 'list' ? 'active' : ''}`}
                                        onClick={() => setViewMode('list')}
                                        title={isKa ? 'ცხრილური / სიის ხედი' : 'Detailed List View'}
                                    >
                                        <i className="fas fa-list" />
                                    </button>
                                </div>
                            </div>

                        </div>

                        {/* Category Filter Tabs Switcher */}
                        <div className="prod-cat-tabs-row mt-3 pt-3">
                            <button
                                className={`prod-cat-tab ${selectedCategory === 'all' ? 'active' : ''}`}
                                onClick={() => setSelectedCategory('all')}
                            >
                                <span>🌐</span>
                                <span>{isKa ? 'ყველა' : 'All Solutions'}</span>
                                <span className="cat-count-badge">{products.length}</span>
                            </button>

                            {categories.map(cat => {
                                const count = products.filter(p => String(p.category_id) === String(cat.id)).length;
                                return (
                                    <button
                                        key={cat.id}
                                        className={`prod-cat-tab ${selectedCategory === String(cat.id) ? 'active' : ''}`}
                                        onClick={() => setSelectedCategory(String(cat.id))}
                                    >
                                        <span>{cat.icon || '📁'}</span>
                                        <span>{isKa ? cat.name_ka : (cat.name_en || cat.name_ka)}</span>
                                        {count > 0 && <span className="cat-count-badge">{count}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Results Counter & Active Filter Tags */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4 px-2">
                        <div className="d-flex align-items-center gap-2">
                            {loading && (
                                <span className="prod-loading-badge">
                                    <span className="prod-loading-dot" />
                                    {isKa ? 'იტვირთება...' : 'Loading...'}
                                </span>
                            )}
                            <span className="prod-results-count">
                                {isKa 
                                    ? `ნაჩვენებია ${processedProducts.length} გადაწყვეტილება ${products.length}-დან` 
                                    : `Showing ${processedProducts.length} of ${products.length} software solutions`}
                            </span>
                        </div>

                        {(selectedCategory !== 'all' || selectedMarket !== 'all' || searchTerm || sortBy !== 'featured') && (
                            <button className="prod-reset-btn" onClick={resetFilters}>
                                <i className="fas fa-undo-alt me-1" />
                                <span>{isKa ? 'ფილტრების გასუფთავება' : 'Reset Filters'}</span>
                            </button>
                        )}
                    </div>

                    {/* Product Cards Listing */}
                    {processedProducts.length === 0 ? (
                        <div className="prod-empty-state text-center py-5">
                            <div className="empty-icon mb-3">🔍</div>
                            <h3 className="text-white mb-2">{isKa ? 'გადაწყვეტილება ვერ მოიძებნა' : 'No Matching Solutions Found'}</h3>
                            <p className="text-white-50 mx-auto mb-4" style={{ maxWidth: '420px' }}>
                                {isKa 
                                    ? 'არჩეული ფილტრების შესაბამისი პროდუქტი არ არსებობს. სცადეთ ძებნის პარამეტრების შეცვლა.' 
                                    : 'No software suites match your active criteria. Try broadening your search or resetting filters.'}
                            </p>
                            <button className="orbx-btn" onClick={resetFilters}>
                                {isKa ? 'ფილტრების გასუფთავება' : 'Reset All Filters'}
                            </button>
                        </div>
                    ) : (
                        <AnimatePresence>
                            {/* Layout Mode 1: 3D Software Box Mode */}
                            {viewMode === 'box' && (
                                <div className="row g-4">
                                    {processedProducts.map((product, idx) => {
                                        const productName = isKa ? product.name_ka : (product.name_en || product.name_ka);
                                        const productTagline = isKa ? product.tagline_ka : (product.tagline_en || product.tagline_ka);
                                        const features = (isKa ? product.features_ka : (product.features_en?.length ? product.features_en : product.features_ka)) || [];
                                        const detailUrl = `/${currentLang}/products/${product.slug || product.id}`;
                                        const accentColor = product.badge_color || '#6c5ce7';

                                        return (
                                            <motion.div 
                                                className="col-lg-4 col-md-6" 
                                                key={product.id || idx}
                                                layout
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, scale: 0.95 }}
                                                transition={{ duration: 0.35, delay: Math.min(idx * 0.04, 0.25) }}
                                            >
                                                <div 
                                                    className="prod-box-card"
                                                    style={{
                                                        '--box-accent': accentColor,
                                                        borderTop: `4px solid ${accentColor}`
                                                    }}
                                                >
                                                    {/* 3D Box Mockup Area */}
                                                    <div className="box-mockup-wrapper">
                                                        <SoftwareBoxMockup 
                                                            product={product} 
                                                            lang={currentLang}
                                                            onClick={() => navigate(detailUrl)}
                                                        />
                                                    </div>

                                                    {/* Card Details */}
                                                    <div className="card-body-content">
                                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                                            <span className="card-cat-label">
                                                                {isKa ? (product.category_name_ka || 'პლატფორმა') : (product.category_name_en || 'Platform')}
                                                            </span>
                                                            <div className="d-flex gap-1">
                                                                {product.is_featured === 1 && (
                                                                    <span className="card-featured-chip">⭐ Featured</span>
                                                                )}
                                                                <span className="card-market-chip">
                                                                    {getMarketLabel(product.target_market)}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <h3 className="card-prod-title">
                                                            <Link to={detailUrl}>{productName}</Link>
                                                        </h3>

                                                        {productTagline && (
                                                            <p className="card-prod-tagline">
                                                                {productTagline}
                                                            </p>
                                                        )}

                                                        {features.length > 0 && (
                                                            <div className="card-features-list mb-3 flex-grow-1">
                                                                {features.slice(0, 3).map((f, fIdx) => (
                                                                    <div key={fIdx} className="card-feature-item">
                                                                        <i className="fas fa-check-circle" style={{ color: accentColor }} />
                                                                        <span>{f}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Pricing */}
                                                        <div className="card-pricing-block mb-3">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <span className="price-type-text">{isKa ? 'საწყისი / დანერგვა' : 'Setup / Deployment'}</span>
                                                                    <div className="price-bold text-info">
                                                                        {parseFloat(product.setup_price) > 0 
                                                                            ? `₾${parseFloat(product.setup_price).toLocaleString()}` 
                                                                            : (isKa ? 'მოთხოვნით' : 'On Request')}
                                                                    </div>
                                                                </div>
                                                                {parseFloat(product.annual_price) > 0 && (
                                                                    <div className="text-end">
                                                                        <span className="price-type-text">{isKa ? 'წლიური ARR' : 'Annual ARR'}</span>
                                                                        <div className="price-bold text-success">
                                                                            ₾{parseFloat(product.annual_price).toLocaleString()} <small>/yr</small>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="d-flex gap-2">
                                                            <Link to={detailUrl} className="card-btn-primary">
                                                                <span>{isKa ? 'დეტალურად ნახვა' : 'View Details'}</span>
                                                                <i className="fas fa-arrow-right" />
                                                            </Link>
                                                            {product.demo_url && (
                                                                <a 
                                                                    href={product.demo_url} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer" 
                                                                    className="card-btn-demo"
                                                                    title="Live Demo"
                                                                >
                                                                    <i className="fas fa-external-link-alt" />
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Layout Mode 2: Modern Corporate Card Mode */}
                            {viewMode === 'card' && (
                                <div className="row g-4">
                                    {processedProducts.map((product, idx) => {
                                        const productName = isKa ? product.name_ka : (product.name_en || product.name_ka);
                                        const productTagline = isKa ? product.tagline_ka : (product.tagline_en || product.tagline_ka);
                                        const features = (isKa ? product.features_ka : (product.features_en?.length ? product.features_en : product.features_ka)) || [];
                                        const detailUrl = `/${currentLang}/products/${product.slug || product.id}`;
                                        const accentColor = product.badge_color || '#6c5ce7';

                                        return (
                                            <motion.div 
                                                className="col-lg-4 col-md-6" 
                                                key={product.id || idx}
                                                layout
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, scale: 0.95 }}
                                                transition={{ duration: 0.35, delay: Math.min(idx * 0.04, 0.25) }}
                                            >
                                                <div 
                                                    className="prod-modern-card"
                                                    style={{ '--card-accent': accentColor, borderTop: `4px solid ${accentColor}` }}
                                                >
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <div 
                                                            className="modern-card-icon"
                                                            style={{ background: `${accentColor}18`, borderColor: `${accentColor}40` }}
                                                        >
                                                            {product.icon || '📦'}
                                                        </div>
                                                        <div className="d-flex gap-1">
                                                            {product.is_featured === 1 && (
                                                                <span className="card-featured-chip">⭐ Featured</span>
                                                            )}
                                                            <span className="card-market-chip">
                                                                {getMarketLabel(product.target_market)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <span className="card-cat-label">
                                                        {isKa ? (product.category_name_ka || 'პლატფორმა') : (product.category_name_en || 'Platform')}
                                                    </span>

                                                    <h3 className="card-prod-title">
                                                        <Link to={detailUrl}>{productName}</Link>
                                                    </h3>

                                                    {productTagline && (
                                                        <p className="card-prod-tagline">
                                                            {productTagline}
                                                        </p>
                                                    )}

                                                    {product.tech_stack && (
                                                        <div className="modern-tech-chips mb-3">
                                                            {product.tech_stack.split(',').slice(0, 3).map((tech, tIdx) => (
                                                                <span key={tIdx} className="tech-chip">{tech.trim()}</span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {features.length > 0 && (
                                                        <div className="card-features-list mb-3 flex-grow-1">
                                                            {features.slice(0, 3).map((f, fIdx) => (
                                                                <div key={fIdx} className="card-feature-item">
                                                                    <i className="fas fa-check-circle" style={{ color: accentColor }} />
                                                                    <span>{f}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className="card-pricing-block mb-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <span className="price-type-text">{isKa ? 'საწყისი / დანერგვა' : 'Setup / Deployment'}</span>
                                                                <div className="price-bold text-info">
                                                                    {parseFloat(product.setup_price) > 0 
                                                                        ? `₾${parseFloat(product.setup_price).toLocaleString()}` 
                                                                        : (isKa ? 'მოთხოვნით' : 'On Request')}
                                                                </div>
                                                            </div>
                                                            {parseFloat(product.annual_price) > 0 && (
                                                                <div className="text-end">
                                                                    <span className="price-type-text">{isKa ? 'წლიური ARR' : 'Annual ARR'}</span>
                                                                    <div className="price-bold text-success">
                                                                        ₾{parseFloat(product.annual_price).toLocaleString()} <small>/yr</small>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="d-flex gap-2">
                                                        <Link to={detailUrl} className="card-btn-primary">
                                                            <span>{isKa ? 'დეტალურად' : 'View Details'}</span>
                                                            <i className="fas fa-arrow-right" />
                                                        </Link>
                                                        {product.demo_url && (
                                                            <a 
                                                                href={product.demo_url} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer" 
                                                                className="card-btn-demo"
                                                                title="Live Demo"
                                                            >
                                                                <i className="fas fa-external-link-alt" />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Layout Mode 3: Detailed List Mode */}
                            {viewMode === 'list' && (
                                <div className="d-flex flex-column gap-3">
                                    {processedProducts.map((product, idx) => {
                                        const productName = isKa ? product.name_ka : (product.name_en || product.name_ka);
                                        const productTagline = isKa ? product.tagline_ka : (product.tagline_en || product.tagline_ka);
                                        const features = (isKa ? product.features_ka : (product.features_en?.length ? product.features_en : product.features_ka)) || [];
                                        const detailUrl = `/${currentLang}/products/${product.slug || product.id}`;
                                        const accentColor = product.badge_color || '#6c5ce7';

                                        return (
                                            <motion.div 
                                                className="prod-list-card"
                                                key={product.id || idx}
                                                layout
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.2) }}
                                                style={{ '--card-accent': accentColor, borderLeft: `5px solid ${accentColor}` }}
                                            >
                                                <div className="row g-3 align-items-center">
                                                    <div className="col-lg-5 col-md-6">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div 
                                                                className="list-icon-pill"
                                                                style={{ background: `${accentColor}18`, borderColor: `${accentColor}40` }}
                                                            >
                                                                {product.icon || '📦'}
                                                            </div>
                                                            <div>
                                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                                    <span className="card-cat-label mb-0">
                                                                        {isKa ? (product.category_name_ka || 'პლატფორმა') : (product.category_name_en || 'Platform')}
                                                                    </span>
                                                                    {product.is_featured === 1 && (
                                                                        <span className="card-featured-chip">⭐</span>
                                                                    )}
                                                                </div>
                                                                <h4 className="list-prod-title mb-1">
                                                                    <Link to={detailUrl}>{productName}</Link>
                                                                </h4>
                                                                <p className="list-prod-tagline mb-0">{productTagline}</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="col-lg-4 d-none d-lg-block">
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {features.slice(0, 3).map((f, fIdx) => (
                                                                <span key={fIdx} className="list-feat-pill">
                                                                    <i className="fas fa-check text-success me-1" />
                                                                    {f}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="col-lg-3 col-md-6 text-md-end">
                                                        <div className="d-flex align-items-center justify-content-md-end gap-3 flex-wrap">
                                                            <div>
                                                                <div className="price-bold text-info">
                                                                    {parseFloat(product.setup_price) > 0 
                                                                        ? `₾${parseFloat(product.setup_price).toLocaleString()}` 
                                                                        : (isKa ? 'მოთხოვნით' : 'Quote')}
                                                                </div>
                                                                {parseFloat(product.annual_price) > 0 && (
                                                                    <small className="text-success">
                                                                        ₾{parseFloat(product.annual_price).toLocaleString()}/yr
                                                                    </small>
                                                                )}
                                                            </div>
                                                            <Link to={detailUrl} className="card-btn-primary px-3 py-2">
                                                                <span>{isKa ? 'ნახვა' : 'View'}</span>
                                                                <i className="fas fa-arrow-right" />
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </AnimatePresence>
                    )}

                    {/* Bottom Custom Solution Banner */}
                    <div className="prod-custom-cta-card mt-80 text-center">
                        <div className="cta-sparkle-pill mb-3">
                            <i className="fas fa-sparkles me-2 text-warning" />
                            <span>{isKa ? 'ინდივიდუალური არქიტექტურა' : 'CUSTOM SOLUTION ARCHITECTURE'}</span>
                        </div>
                        <h2 className="cta-headline mb-3">
                            {isKa 
                                ? 'ვერ იპოვეთ თქვენს მუნიციპალიტეტზე მორგებული პაკეტი?' 
                                : 'Need a Tailored Municipal or Enterprise Software Suite?'}
                        </h2>
                        <p className="cta-paragraph mx-auto mb-4">
                            {isKa 
                                ? 'ჩვენი საინჟინრო გუნდი ახდენს სისტემების სრულ ინდივიდუალურ ადაპტაციას, ინტეგრირებას არსებულ მონაცემთა ბაზებთან და ISO 27001 უსაფრთხოების აუდიტს.'
                                : 'Our engineering practice designs bespoke workflow platforms, connects with legacy government registries, and provides complete on-premise deployment with support SLA.'}
                        </p>
                        <Link to={`/${currentLang}/contact`} className="cta-action-btn">
                            <i className="fas fa-file-signature me-2" />
                            <span>{isKa ? 'ინდივიდუალური პაკეტის მოთხოვნა' : 'Request Custom Suite Architecture'}</span>
                        </Link>
                    </div>

                </div>
            </main>

            <style>{`
                .prod-catalog-page {
                    background: #060918;
                    color: #e2e8f0;
                    position: relative;
                    min-height: 100vh;
                }
                .prod-ambient-glow {
                    position: absolute;
                    top: 100px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 1000px;
                    height: 500px;
                    background: radial-gradient(ellipse at center, rgba(108, 92, 231, 0.18) 0%, rgba(56, 189, 248, 0.08) 45%, transparent 70%);
                    filter: blur(90px);
                    pointer-events: none;
                    z-index: 0;
                }

                .prod-catalog-eyebrow {
                    display: inline-flex;
                    align-items: center;
                    color: #818cf8;
                    font-size: 12.5px;
                    font-weight: 800;
                    letter-spacing: 2.5px;
                    text-transform: uppercase;
                }
                .prod-catalog-title {
                    font-size: clamp(2rem, 4vw, 3.2rem);
                    font-weight: 800;
                    color: #ffffff;
                    letter-spacing: -0.02em;
                    line-height: 1.25;
                }
                .prod-catalog-desc {
                    color: rgba(255, 255, 255, 0.65);
                    font-size: 15.5px;
                    line-height: 1.7;
                    max-width: 740px;
                }

                /* Controls Card */
                .prod-controls-card {
                    background: rgba(14, 20, 48, 0.7);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 20px;
                    padding: 22px 24px;
                    backdrop-filter: blur(14px);
                    position: relative;
                    z-index: 2;
                }

                .prod-search-wrap {
                    position: relative;
                    width: 100%;
                }
                .prod-search-icon {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: rgba(255, 255, 255, 0.4);
                    font-size: 13px;
                }
                .prod-search-input {
                    width: 100%;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    border-radius: 12px;
                    padding: 10px 36px 10px 38px;
                    color: #fff;
                    font-size: 13.5px;
                    outline: none;
                    transition: all 0.2s;
                }
                .prod-search-input:focus {
                    background: rgba(255, 255, 255, 0.08);
                    border-color: #6c5ce7;
                    box-shadow: 0 0 15px rgba(108, 92, 231, 0.35);
                }
                .prod-search-clear {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: transparent;
                    border: none;
                    color: rgba(255, 255, 255, 0.5);
                    cursor: pointer;
                }

                .prod-ctrl-label {
                    font-size: 12px;
                    font-weight: 700;
                    color: rgba(255, 255, 255, 0.5);
                    white-space: nowrap;
                }
                .prod-select {
                    width: 100%;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    border-radius: 12px;
                    padding: 10px 14px;
                    color: #fff;
                    font-size: 13px;
                    font-weight: 600;
                    outline: none;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .prod-select:focus {
                    border-color: #6c5ce7;
                }
                .prod-select option {
                    background: #090e24;
                    color: #fff;
                }

                /* View Switcher */
                .prod-view-switch-btn-group {
                    display: inline-flex;
                    background: rgba(255, 255, 255, 0.06);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 12px;
                    padding: 3px;
                    gap: 3px;
                }
                .view-switch-btn {
                    width: 36px;
                    height: 36px;
                    border-radius: 9px;
                    border: none;
                    background: transparent;
                    color: rgba(255, 255, 255, 0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .view-switch-btn:hover {
                    color: #fff;
                    background: rgba(255, 255, 255, 0.1);
                }
                .view-switch-btn.active {
                    background: #6c5ce7;
                    color: #fff;
                    box-shadow: 0 2px 10px rgba(108, 92, 231, 0.45);
                }

                /* Category Tabs */
                .prod-cat-tabs-row {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 8px;
                    border-top: 1px solid rgba(255, 255, 255, 0.08);
                }
                .prod-cat-tab {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 14px;
                    border-radius: 30px;
                    background: rgba(255, 255, 255, 0.04);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 12.5px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .prod-cat-tab:hover {
                    background: rgba(255, 255, 255, 0.08);
                    color: #fff;
                }
                .prod-cat-tab.active {
                    background: #6c5ce7;
                    color: #fff;
                    border-color: #6c5ce7;
                    box-shadow: 0 4px 15px rgba(108, 92, 231, 0.35);
                }
                .cat-count-badge {
                    font-size: 10px;
                    padding: 1px 6px;
                    border-radius: 10px;
                    background: rgba(0, 0, 0, 0.3);
                }

                /* Result Counter & Reset */
                .prod-results-count {
                    font-size: 13px;
                    font-weight: 600;
                    color: rgba(255, 255, 255, 0.6);
                }
                .prod-loading-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    font-weight: 600;
                    color: #a29bfe;
                    background: rgba(108, 92, 231, 0.12);
                    border: 1px solid rgba(108, 92, 231, 0.25);
                    padding: 3px 10px;
                    border-radius: 20px;
                }
                .prod-loading-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #6c5ce7;
                    animation: loadingPulse 1s ease-in-out infinite;
                }
                @keyframes loadingPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.4; transform: scale(0.7); }
                }
                .prod-reset-btn {
                    background: transparent;
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 12px;
                    padding: 4px 12px;
                    border-radius: 20px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .prod-reset-btn:hover {
                    color: #fff;
                    border-color: #f43f5e;
                    background: rgba(244, 63, 94, 0.15);
                }

                /* 3D Box Card Styling */
                .prod-box-card {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    background: rgba(14, 19, 44, 0.78);
                    backdrop-filter: blur(14px);
                    border-radius: 22px;
                    border-left: 1px solid rgba(255, 255, 255, 0.08);
                    border-right: 1px solid rgba(255, 255, 255, 0.08);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                    padding: 24px 20px 22px;
                    transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .prod-box-card:hover {
                    background: rgba(19, 25, 58, 0.92);
                    border-color: rgba(255, 255, 255, 0.2);
                    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.5), 0 0 35px rgba(108, 92, 231, 0.18);
                    transform: translateY(-6px);
                }
                .box-mockup-wrapper {
                    padding: 8px 0 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* Modern Card Styling */
                .prod-modern-card {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    background: rgba(14, 19, 44, 0.78);
                    backdrop-filter: blur(14px);
                    border-radius: 22px;
                    border-left: 1px solid rgba(255, 255, 255, 0.08);
                    border-right: 1px solid rgba(255, 255, 255, 0.08);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                    padding: 28px 24px 24px;
                    transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .prod-modern-card:hover {
                    background: rgba(19, 25, 58, 0.92);
                    border-color: rgba(255, 255, 255, 0.2);
                    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.5), 0 0 35px rgba(108, 92, 231, 0.18);
                    transform: translateY(-6px);
                }
                .modern-card-icon {
                    width: 50px;
                    height: 50px;
                    border-radius: 14px;
                    border: 1px solid;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                }
                .modern-tech-chips {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                }
                .tech-chip {
                    font-size: 11px;
                    font-weight: 600;
                    padding: 2px 8px;
                    border-radius: 4px;
                    background: rgba(255, 255, 255, 0.06);
                    color: #94a3b8;
                }

                /* List Card Styling */
                .prod-list-card {
                    background: rgba(14, 20, 48, 0.75);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 18px;
                    padding: 20px 24px;
                    backdrop-filter: blur(12px);
                    transition: all 0.25s;
                }
                .prod-list-card:hover {
                    background: rgba(19, 27, 64, 0.9);
                    transform: translateX(4px);
                    border-color: rgba(255, 255, 255, 0.18);
                }
                .list-icon-pill {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    border: 1px solid;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    flex-shrink: 0;
                }
                .list-prod-title {
                    font-size: 1.15rem;
                    font-weight: 800;
                    margin: 0;
                }
                .list-prod-title a {
                    color: #fff;
                    text-decoration: none;
                }
                .list-prod-title a:hover {
                    color: #a5b4fc;
                }
                .list-prod-tagline {
                    font-size: 12.5px;
                    color: rgba(255, 255, 255, 0.6);
                }
                .list-feat-pill {
                    font-size: 11.5px;
                    background: rgba(255, 255, 255, 0.05);
                    padding: 3px 8px;
                    border-radius: 6px;
                    color: rgba(255, 255, 255, 0.8);
                }

                /* Card Shared Elements */
                .card-cat-label {
                    display: block;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1.2px;
                    color: #a29bfe;
                }
                .card-featured-chip {
                    font-size: 10px;
                    font-weight: 700;
                    padding: 3px 8px;
                    border-radius: 6px;
                    background: rgba(245, 158, 11, 0.2);
                    color: #fbbf24;
                    border: 1px solid rgba(245, 158, 11, 0.35);
                }
                .card-market-chip {
                    font-size: 10px;
                    font-weight: 600;
                    padding: 3px 8px;
                    border-radius: 6px;
                    background: rgba(255, 255, 255, 0.06);
                    color: rgba(255, 255, 255, 0.7);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }
                .card-prod-title {
                    font-size: 1.25rem;
                    font-weight: 800;
                    line-height: 1.35;
                    margin: 6px 0 8px;
                }
                .card-prod-title a {
                    color: #ffffff;
                    text-decoration: none;
                    transition: color 0.2s;
                }
                .card-prod-title a:hover {
                    color: #a29bfe;
                }
                .card-prod-tagline {
                    color: rgba(255, 255, 255, 0.62);
                    font-size: 13px;
                    line-height: 1.55;
                    margin: 0 0 12px;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .card-features-list {
                    display: flex;
                    flex-direction: column;
                    gap: 7px;
                }
                .card-feature-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 12.5px;
                    color: rgba(255, 255, 255, 0.85);
                }
                .card-pricing-block {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.07);
                    border-radius: 12px;
                    padding: 12px 14px;
                }
                .price-type-text {
                    display: block;
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: rgba(255, 255, 255, 0.45);
                    margin-bottom: 2px;
                }
                .price-bold {
                    font-weight: 800;
                    font-size: 1.15rem;
                }
                .card-btn-primary {
                    flex-grow: 1;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    background: rgba(108, 92, 231, 0.16);
                    border: 1px solid rgba(108, 92, 231, 0.45);
                    color: #fff;
                    font-weight: 700;
                    font-size: 13px;
                    padding: 10px 16px;
                    border-radius: 10px;
                    text-decoration: none;
                    transition: all 0.25s ease;
                }
                .card-btn-primary:hover {
                    background: #6c5ce7;
                    color: #fff;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 16px rgba(108, 92, 231, 0.4);
                }
                .card-btn-demo {
                    width: 42px;
                    height: 42px;
                    border-radius: 10px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    color: rgba(255, 255, 255, 0.8);
                    text-decoration: none;
                    transition: all 0.25s ease;
                }
                .card-btn-demo:hover {
                    background: rgba(255, 255, 255, 0.15);
                    color: #fff;
                }

                /* Bottom Custom CTA */
                .prod-custom-cta-card {
                    background: linear-gradient(145deg, rgba(108, 92, 231, 0.22) 0%, rgba(14, 20, 52, 0.8) 100%);
                    border: 1px solid rgba(108, 92, 231, 0.4);
                    border-radius: 28px;
                    padding: 50px 30px;
                    backdrop-filter: blur(14px);
                }
                .cta-sparkle-pill {
                    display: inline-flex;
                    align-items: center;
                    background: rgba(255, 255, 255, 0.08);
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    padding: 5px 16px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.5px;
                    color: #fff;
                }
                .cta-headline {
                    font-size: clamp(1.6rem, 3.2vw, 2.3rem);
                    font-weight: 800;
                    color: #ffffff;
                }
                .cta-paragraph {
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 15px;
                    line-height: 1.7;
                    max-width: 680px;
                }
                .cta-action-btn {
                    display: inline-flex;
                    align-items: center;
                    padding: 14px 34px;
                    border-radius: 50px;
                    background: linear-gradient(135deg, #6c5ce7, #4f46e5);
                    color: #fff;
                    font-weight: 700;
                    font-size: 14px;
                    text-decoration: none;
                    transition: all 0.3s;
                    box-shadow: 0 10px 25px rgba(108, 92, 231, 0.35);
                }
                .cta-action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 14px 32px rgba(108, 92, 231, 0.5);
                    color: #fff;
                }
            `}</style>
        </Layout>
    );
};

export default ProductsPage;
